/**
 * Ficha de cliente: alta, edición, archivo y borrado — `TDS-CORE` Parte 1 §4.
 *
 * - Identificación fiscal opcional (cliente provisional, `URS §10`).
 * - La clave de idempotencia se fija al abrir el formulario de alta: reintentar
 *   el mismo "Guardar" (red cortada, doble clic) no crea dos clientes ni gasta
 *   dos plazas (`TDS-000 §5.1`).
 * - Duplicado de NIF: se ofrece abrir el existente (`URS §13`).
 * - Borrar pide una segunda confirmación en la propia tarjeta, sin diálogos
 *   improvisados (`OD-0017 D-01`), y sólo se ofrece si la API dice `deletable`.
 */
import { useEffect, useState } from "react";
import {
  api,
  ESTADO_VALIDACION,
  type Cliente,
  type DatosCliente,
  type ErrorApi,
  type ObligacionRetener,
  type Oportunidad,
  type TipoId,
} from "../api/cliente";
import { t } from "../i18n/es";
import { EnlacesComprobarAeat } from "../ui/EnlacesComprobarAeat";
import { importeLegible } from "../ui/formato";
import { opcionesPais } from "../ui/paises";
import { opcionDePlazo, pasaDelMaximoEntreEmpresas, PLAZOS_DE_PAGO } from "../ui/plazo-de-pago";
import { constaComoEmpresa } from "../ui/vencimiento";
import { Aviso, Boton, Campo, FilaPulsable, Selector, Tarjeta } from "../ui/primitivas";
import { BloqueActividades } from "./Actividades";
import { PantallaFacturasImportadas } from "./PantallaFacturasImportadas";

const VACIO: DatosCliente = {
  displayName: "",
  legalName: null,
  taxIdType: null,
  taxIdValue: null,
  country: "ES",
  addressLine: null,
  postalCode: null,
  city: null,
  province: null,
  email: null,
  phone: null,
  notes: null,
  withholdingObligation: "DESCONOCIDO",
  paymentTermsDays: null,
};

/** OD-0062 E-03: sin plazo (la regla general), los de la lista, u «Otro». */
const OPCIONES_PLAZO: ReadonlyArray<{ readonly valor: string; readonly texto: string }> = [
  { valor: "", texto: t.cliente.plazos.ninguno },
  ...PLAZOS_DE_PAGO.map((p) => ({ valor: p, texto: t.cliente.plazos.dias(p) })),
  { valor: "OTRO", texto: t.cliente.plazos.otro },
];

/** OD-0029 D-03: pagador obligado a retener. Sólo sirve para SUGERIR. */
const OBLIGACIONES: ReadonlyArray<{ readonly valor: ObligacionRetener; readonly texto: string }> = [
  { valor: "DESCONOCIDO", texto: t.cliente.obligaciones.DESCONOCIDO },
  { valor: "SI", texto: t.cliente.obligaciones.SI },
  { valor: "NO", texto: t.cliente.obligaciones.NO },
];

type OpcionTipo = TipoId | "NINGUNO";

const TIPOS: ReadonlyArray<{ readonly valor: OpcionTipo; readonly texto: string }> = [
  { valor: "NINGUNO", texto: t.cliente.sinIdentificacion },
  ...(["NIF_ES_PERSON", "NIE_ES", "NIF_ES_ENTITY", "VAT_EU", "FOREIGN_ID", "OTHER"] as const).map((v) => ({
    valor: v,
    texto: t.cliente.tipos[v],
  })),
];

function datosDe(c: Cliente): DatosCliente {
  const { id, taxIdNormalized, taxIdControlWarning, status, archiveReason, deletable, ...datos } = c;
  void id;
  void taxIdNormalized;
  void taxIdControlWarning;
  void status;
  void archiveReason;
  void deletable;
  return datos;
}

/** "" -> null: la API distingue "sin dato" de un texto vacío. */
function limpiar(d: DatosCliente): DatosCliente {
  const n = (v: string | null) => (v === null || v.trim() === "" ? null : v);
  return {
    ...d,
    legalName: n(d.legalName),
    taxIdValue: d.taxIdType === null ? null : n(d.taxIdValue),
    addressLine: n(d.addressLine),
    postalCode: n(d.postalCode),
    city: n(d.city),
    province: n(d.province),
    email: n(d.email),
    phone: n(d.phone),
    notes: n(d.notes),
    paymentTermsDays: typeof d.paymentTermsDays === "string" ? n(d.paymentTermsDays) : d.paymentTermsDays,
  };
}

export function PantallaCliente({
  id,
  hayConexion,
  onVolver,
  onAbrir,
  onAbrirOportunidad = null,
  onNuevaOportunidad = null,
  onSesionCaducada,
}: {
  /** `null` = alta. */
  id: string | null;
  hayConexion: boolean;
  onVolver: () => void;
  onAbrir: (id: string) => void;
  /** OD-0048 D-07: sus oportunidades abiertas, solo si el plan tiene CRM. */
  onAbrirOportunidad?: ((id: string) => void) | null;
  onNuevaOportunidad?: ((clienteId: string) => void) | null;
  onSesionCaducada: () => void;
}) {
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [datos, setDatos] = useState<DatosCliente>(VACIO);
  const [cargando, setCargando] = useState(id !== null);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error" | "aviso" | "info"; texto: string } | null>(null);
  const [duplicadoDe, setDuplicadoDe] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  // «Otro» se queda elegido aunque lo escrito coincida con uno de la lista.
  const [plazoOtro, setPlazoOtro] = useState(false);
  // Una clave por formulario de alta, no por pulsación (TDS-000 §5.1).
  const [clave] = useState(() => crypto.randomUUID());
  // `null` = no se ensena el bloque: sin CRM en el plan (402), sin permiso
  // (403) o sin poder cargarlo. No es un error del cliente.
  const [oportunidades, setOportunidades] = useState<readonly Oportunidad[] | null>(null);

  // Depende de SI hay CRM, no de la funcion: el panel la recrea en cada render.
  const conCrm = onAbrirOportunidad !== null;
  useEffect(() => {
    if (id === null || !conCrm) return;
    void (async () => {
      const r = await api.listarOportunidades("OPEN", id);
      setOportunidades(r.ok ? r.datos.oportunidades : null);
    })();
  }, [id, conCrm]);

  useEffect(() => {
    if (id === null) return;
    void (async () => {
      const r = await api.obtenerCliente(id);
      setCargando(false);
      if (r.ok) {
        setCliente(r.datos);
        setDatos(datosDe(r.datos));
        setPlazoOtro(opcionDePlazo(r.datos.paymentTermsDays) === "OTRO");
      } else if (r.estado === 401) onSesionCaducada();
      else setMensaje({ tono: "error", texto: r.estado === 404 ? t.cliente.noEncontrado : t.comun.errorGenerico });
    })();
  }, [id, onSesionCaducada]);

  const texto = (campo: keyof DatosCliente) => (v: string) => setDatos((d) => ({ ...d, [campo]: v }));
  const valor = (campo: keyof DatosCliente): string => (datos[campo] as string | null) ?? "";
  const errorDe = (campo: string): string | null =>
    errores[campo] === undefined ? null : (t.cliente.errores[errores[campo]] ?? t.comun.errorGenerico);
  const opcionPlazo = plazoOtro ? "OTRO" : opcionDePlazo(datos.paymentTermsDays);
  // Ley 3/2004, art. 4.3: entre empresas, como mucho 60 días. Se avisa, no se impide.
  const plazoLargo =
    constaComoEmpresa({ taxIdType: datos.taxIdType, withholdingObligation: datos.withholdingObligation }) &&
    pasaDelMaximoEntreEmpresas(datos.paymentTermsDays);

  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje({ tono: "aviso", texto: t.cliente.sinPermiso });
    if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores(r.error.campos);
    if (r.estado === 409 && r.error?.motivo === "DUPLICATE_TAX_ID") {
      const existente = r.error.detalles.existingCustomerId;
      setDuplicadoDe(typeof existente === "string" ? existente : null);
      return setMensaje({ tono: "aviso", texto: t.cliente.duplicado });
    }
    if (r.estado === 409 && r.error?.motivo === "HAS_ISSUED_INVOICES") {
      return setMensaje({ tono: "aviso", texto: t.cliente.noEliminable });
    }
    // 429 QUOTA_EXCEEDED: el mensaje de la API ya dice límite y qué hacer (URS §110).
    if (r.estado === 429 && r.error !== null) return setMensaje({ tono: "aviso", texto: r.error.message });
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico });
  }

  function limpiarAvisos(): void {
    setMensaje(null);
    setErrores({});
    setDuplicadoDe(null);
  }

  async function guardar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    limpiarAvisos();
    setOcupado(true);
    const r = cliente === null ? await api.crearCliente(limpiar(datos), clave) : await api.editarCliente(cliente.id, limpiar(datos));
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    const eraAlta = cliente === null;
    setCliente(r.datos);
    setDatos(datosDe(r.datos));
    setMensaje({ tono: "exito", texto: eraAlta ? t.cliente.creado : t.cliente.guardado });
  }

  async function archivar(): Promise<void> {
    if (cliente === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.archivarCliente(cliente.id);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    setCliente(r.datos);
    setMensaje({ tono: "info", texto: t.cliente.archivado });
  }

  async function eliminar(): Promise<void> {
    if (cliente === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.eliminarCliente(cliente.id);
    setOcupado(false);
    setConfirmandoBorrado(false);
    if (!r.ok) return rechazo(r);
    onVolver();
  }

  if (cargando) {
    return (
      <Tarjeta titulo={t.cliente.tituloEditar}>
        <p>{t.clientes.cargando}</p>
      </Tarjeta>
    );
  }

  return (
    <>
      <Boton variante="secundario" onClick={onVolver}>
        {t.cliente.volver}
      </Boton>
      <Tarjeta titulo={cliente === null ? t.cliente.tituloNuevo : t.cliente.tituloEditar}>
        <form onSubmit={guardar} noValidate>
          <Campo id="cli-nombre" etiqueta={t.cliente.displayName} valor={valor("displayName")} onChange={texto("displayName")} ayuda={t.cliente.ayudaNombre} error={errorDe("displayName")} />
          <Campo id="cli-razon" etiqueta={t.cliente.legalName} valor={valor("legalName")} onChange={texto("legalName")} autoComplete="organization" requerido={false} error={errorDe("legalName")} />
          <div className="fila-campos">
            <Selector
              id="cli-tipo"
              etiqueta={t.cliente.tipo}
              valor={datos.taxIdType ?? "NINGUNO"}
              opciones={TIPOS}
              onChange={(v) => setDatos((d) => ({ ...d, taxIdType: v === "NINGUNO" ? null : v }))}
              error={errorDe("taxIdType")}
            />
            {datos.taxIdType !== null ? (
              <Campo id="cli-nif" etiqueta={t.cliente.numero} valor={valor("taxIdValue")} onChange={texto("taxIdValue")} autoComplete="off" error={errorDe("taxIdValue")} />
            ) : null}
          </div>
          {datos.taxIdType === null ? <p className="ayuda-neutra">{t.cliente.ayudaProvisional}</p> : null}
          {cliente?.taxIdControlWarning ? <Aviso tono="aviso">{t.cliente.avisoControl}</Aviso> : null}
          {cliente?.taxIdNormalized ? <EnlacesComprobarAeat tipo={cliente.taxIdType} /> : null}
          <div className="fila-campos">
            <Campo id="cli-email" etiqueta={t.cliente.email} valor={valor("email")} onChange={texto("email")} tipo="email" inputMode="email" autoComplete="off" requerido={false} error={errorDe("email")} />
            <Campo id="cli-telefono" etiqueta={t.cliente.telefono} valor={valor("phone")} onChange={texto("phone")} autoComplete="off" requerido={false} error={errorDe("phone")} />
          </div>
          <Campo id="cli-direccion" etiqueta={t.cliente.direccion} valor={valor("addressLine")} onChange={texto("addressLine")} autoComplete="off" requerido={false} error={errorDe("addressLine")} />
          <div className="fila-campos">
            <Campo id="cli-cp" etiqueta={t.cliente.codigoPostal} valor={valor("postalCode")} onChange={texto("postalCode")} autoComplete="off" requerido={false} error={errorDe("postalCode")} />
            <Campo id="cli-municipio" etiqueta={t.cliente.municipio} valor={valor("city")} onChange={texto("city")} autoComplete="off" requerido={false} error={errorDe("city")} />
            <Campo id="cli-provincia" etiqueta={t.cliente.provincia} valor={valor("province")} onChange={texto("province")} autoComplete="off" requerido={false} error={errorDe("province")} />
            <Selector id="cli-pais" etiqueta={t.cliente.pais} valor={datos.country} opciones={opcionesPais(datos.country)} onChange={texto("country")} error={errorDe("country")} />
          </div>
          <Selector
            id="cli-retencion"
            etiqueta={t.cliente.obligacion}
            valor={datos.withholdingObligation}
            opciones={OBLIGACIONES}
            onChange={(v) => setDatos((d) => ({ ...d, withholdingObligation: v }))}
            error={errorDe("withholdingObligation")}
          />
          <p className="ayuda-neutra">{t.cliente.ayudaObligacion}</p>
          <div className="fila-campos">
            <Selector
              id="cli-plazo"
              etiqueta={t.cliente.plazo}
              valor={opcionPlazo}
              opciones={OPCIONES_PLAZO}
              onChange={(v) => {
                setPlazoOtro(v === "OTRO");
                setDatos((d) => ({ ...d, paymentTermsDays: v === "OTRO" ? "" : v === "" ? null : v }));
              }}
              error={opcionPlazo === "OTRO" ? null : errorDe("paymentTermsDays")}
            />
            {opcionPlazo === "OTRO" ? (
              <Campo
                id="cli-plazo-dias"
                etiqueta={t.cliente.plazoDias}
                valor={datos.paymentTermsDays === null ? "" : String(datos.paymentTermsDays)}
                onChange={(v) => setDatos((d) => ({ ...d, paymentTermsDays: v }))}
                inputMode="numeric"
                autoComplete="off"
                error={errorDe("paymentTermsDays")}
              />
            ) : null}
          </div>
          <p className="ayuda-neutra">{t.cliente.ayudaPlazo}</p>
          {plazoLargo ? <Aviso tono="aviso">{t.cliente.plazoLargo}</Aviso> : null}
          <Campo id="cli-notas" etiqueta={t.cliente.notas} valor={valor("notes")} onChange={texto("notes")} autoComplete="off" requerido={false} error={errorDe("notes")} />

          {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
          {duplicadoDe !== null ? (
            <Boton variante="secundario" onClick={() => onAbrir(duplicadoDe)}>
              {t.cliente.abrirExistente}
            </Boton>
          ) : null}
          {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
          <Boton tipo="submit" deshabilitado={ocupado || !hayConexion}>
            {ocupado ? t.cliente.guardando : t.cliente.guardar}
          </Boton>
        </form>
      </Tarjeta>

      {cliente !== null && oportunidades !== null && onAbrirOportunidad !== null ? (
        <Tarjeta titulo={t.oportunidades.enCliente}>
          {oportunidades.length === 0 ? <p className="ayuda-neutra">{t.oportunidades.enClienteVacio}</p> : null}
          {oportunidades.length > 0 ? (
            <ul className="lista-filas">
              {oportunidades.map((o) => (
                <FilaPulsable key={o.id} onClick={() => onAbrirOportunidad(o.id)}>
                  <span className="fila-titulo">{o.title}</span>
                  <span className="ayuda-neutra">
                    {o.estimatedAmount === null ? t.oportunidades.sinImporte : t.oportunidades.importe(importeLegible(o.estimatedAmount))}
                  </span>
                </FilaPulsable>
              ))}
            </ul>
          ) : null}
          {cliente.status === "ACTIVE" && onNuevaOportunidad !== null ? (
            <Boton variante="secundario" onClick={() => onNuevaOportunidad(cliente.id)} deshabilitado={!hayConexion}>
              {t.oportunidades.nueva}
            </Boton>
          ) : null}
        </Tarjeta>
      ) : null}

      {/* OD-0050 D-07: sus tareas y su historial. Solo si el CRM respondio arriba:
          sin Business no se pide nada mas. */}
      {cliente !== null && oportunidades !== null ? <BloqueActividades clienteId={cliente.id} onSesionCaducada={onSesionCaducada} /> : null}
      {/* OD-0057 D-06: sus facturas de otro programa, si se importo alguna. */}
      {cliente !== null ? <PantallaFacturasImportadas clienteId={cliente.id} /> : null}

      {cliente !== null && cliente.status === "ACTIVE" ? (
        <Tarjeta titulo={t.cliente.archivar}>
          <p className="ayuda-neutra">{t.cliente.archivarDetalle}</p>
          <Boton variante="secundario" onClick={() => void archivar()} deshabilitado={ocupado || !hayConexion}>
            {t.cliente.archivar}
          </Boton>
        </Tarjeta>
      ) : null}

      {cliente !== null ? (
        <Tarjeta titulo={t.cliente.eliminar}>
          {!cliente.deletable ? <p className="ayuda-neutra">{t.cliente.noEliminable}</p> : null}
          {cliente.deletable && !confirmandoBorrado ? (
            <Boton variante="secundario" onClick={() => setConfirmandoBorrado(true)} deshabilitado={ocupado || !hayConexion}>
              {t.cliente.eliminar}
            </Boton>
          ) : null}
          {cliente.deletable && confirmandoBorrado ? (
            <>
              <Aviso tono="aviso">{t.cliente.eliminarPregunta}</Aviso>
              <div className="acciones">
                <Boton onClick={() => void eliminar()} deshabilitado={ocupado || !hayConexion}>
                  {t.cliente.siEliminar}
                </Boton>
                <Boton variante="secundario" onClick={() => setConfirmandoBorrado(false)}>
                  {t.cliente.cancelar}
                </Boton>
              </div>
            </>
          ) : null}
        </Tarjeta>
      ) : null}
    </>
  );
}
